import type { User } from '../types';

/** SAP / legacy RFID field names for the employee login code (e.g. LIPL0928). */
const EMPLOYEE_CODE_KEYS = [
  'employeeCode',
  'employee_code',
  'empCode',
  'emp_code',
  'empcode',
  'userID',
  'UserID',
  'user_id',
  'userId',
] as const;

export function pickEmployeeCodeFromUnknown(source: unknown): string | undefined {
  if (source == null || typeof source !== 'object') return undefined;
  const rec = source as Record<string, unknown>;

  for (const key of EMPLOYEE_CODE_KEYS) {
    const v = rec[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }

  const uid = rec.userId;
  if (typeof uid === 'string' && uid.trim() && !uid.includes('@')) return uid.trim();

  const user = rec.user;
  if (user && typeof user === 'object') {
    const nested = pickEmployeeCodeFromUnknown(user);
    if (nested) return nested;
  }

  const data = rec.data;
  if (data && typeof data === 'object') {
    const nested = pickEmployeeCodeFromUnknown(data);
    if (nested) return nested;
  }

  return undefined;
}

function pickEmail(user: User): string | undefined {
  if (typeof user.email === 'string' && user.email.includes('@')) {
    return user.email.trim();
  }
  return undefined;
}

/**
 * Value for SAP `UserID` / `UserId` on RFID APIs.
 * Prefer employee code (legacy: tokenData.user.userId); fall back to email when no code exists.
 */
export function getEmployeeCode(user: User | null | undefined): string {
  if (!user) return '';

  const fromProfile = pickEmployeeCodeFromUnknown(user);
  if (fromProfile) return fromProfile;

  const uid = user.userId;
  if (typeof uid === 'string' && uid.trim()) return uid.trim();

  const email = pickEmail(user);
  if (email) return email;

  return '';
}
